import React, { useState, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { DatePickerInput } from "react-native-paper-dates";

import { ActivityState, Assessment } from "@axio/graphql/generated/graphql";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import { SearchSelectMulti } from "../../../common/form/SearchSelect/SearchSelectMulti";
import CustomButton from "../../../common/general/CustomButton/CustomButton";

type ReviewFormProps = {
  activityData: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  error: string;
  isSubmitting: boolean;
  assessmentsData: Assessment[];
  assessmentsLoading: boolean;
};

const ReviewForm = ({
  activityData,
  onSave,
  onCancel,
  error,
  isSubmitting,
  assessmentsData = [],
  assessmentsLoading = false,
}: ReviewFormProps) => {
  // State
  const [assessments, setAssessments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [showAssessmentChangeWarning, setShowAssessmentChangeWarning] = useState(false);

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      assessmentId: activityData?.hasReviewFor
        ? [{ id: activityData.hasReviewFor.id, name: "Selected Assessment" }]
        : [],
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
    },
  });

  const watchAssessmentId = watch("assessmentId");
  const watchActivityState = watch("activityState");

  // Track original assessment ID for comparison
  const originalAssessmentId = activityData?.hasReviewFor?.id;

  // Process assessments data when it changes
  useEffect(() => {
    if (assessmentsData && assessmentsData.length > 0) {
      // Filter out assessments that already have reviews in any module (unless we're editing that specific review)
      const filteredAssessments = assessmentsData.filter((assessment) => {
        const hasExistingReview = assessment.hasReviews && assessment.hasReviews.length > 0;
        // If we're editing, allow the current assessment even if it has reviews
        if (activityData?.id && activityData?.hasReviewFor?.id === assessment.id) {
          return true;
        }
        // Otherwise, only show assessments without reviews
        return !hasExistingReview;
      });

      const formattedAssessments = filteredAssessments.map((assessment) => {
        const moduleName = assessment.moduleHasAssessment.title || "Untitled Module";
        return {
          id: assessment.id,
          name: `${moduleName}'s Assessment`,
          moduleId: assessment.moduleHasAssessment.id,
          rawData: assessment,
        };
      });
      setAssessments(formattedAssessments);

      // Set default assessment if we have a selected module and no assessment is selected yet
      if (selectedModuleId && (!watchAssessmentId || watchAssessmentId.length === 0)) {
        const defaultAssessment = formattedAssessments.find((a) => a.moduleId === selectedModuleId);
        if (defaultAssessment) {
          setValue("assessmentId", [defaultAssessment]);
        }
      }
    }
  }, [assessmentsData, selectedModuleId, setValue, activityData?.id, activityData?.hasReviewFor?.id]);

  // Set the selected module ID when editing
  useEffect(() => {
    if (activityData?.moduleHasReviews && activityData.moduleHasReviews[0]?.id) {
      setSelectedModuleId(activityData.moduleHasReviews[0].id);
    } else if (activityData?.parentModuleId) {
      setSelectedModuleId(activityData.parentModuleId);
    }
  }, [activityData]);

  // Set the assessment when editing
  useEffect(() => {
    if (activityData?.hasReviewFor && assessments.length > 0) {
      const assessment = assessments.find((a) => a.id === activityData.hasReviewFor.id);
      if (assessment) {
        setValue("assessmentId", [assessment]);
      }
    }
  }, [activityData, assessments, setValue]);

  // Detect assessment changes and show warning
  useEffect(() => {
    if (activityData?.id && originalAssessmentId && watchAssessmentId && watchAssessmentId.length > 0) {
      const currentAssessmentId = watchAssessmentId[0]?.id;
      const hasAssessmentChanged = currentAssessmentId && currentAssessmentId !== originalAssessmentId;
      setShowAssessmentChangeWarning(hasAssessmentChanged);
    } else {
      setShowAssessmentChangeWarning(false);
    }
  }, [watchAssessmentId, originalAssessmentId, activityData?.id]);

  // Form submission
  const onSubmit = (data) => {
    if (!data.assessmentId || data.assessmentId.length === 0) {
      return;
    }

    // Create the activity data object with required fields
    const activityData = {
      assessmentId: data.assessmentId[0].id,
      parentModuleId: selectedModuleId,
      type: "Review",
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    onSave(activityData);
  };

  // Filter assessments based on search query
  const filterAssessments = async (query) => {
    setSearchQuery(query);
    if (!assessments || assessments.length === 0) return [];

    if (!query) return assessments;

    const lowercaseQuery = query.toLowerCase();
    return assessments.filter((assessment) => assessment.name.toLowerCase().includes(lowercaseQuery));
  };

  const hasAssessments = Array.isArray(assessments) && assessments.length > 0;

  return (
    <FormLayout
      title={`${activityData?.id ? "Edit" : "Create"} Review`}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={activityData?.id ? "Save" : "Create"}
      disableSubmit={!hasAssessments || !watchAssessmentId || watchAssessmentId.length === 0}
      error={error}>
      <CustomText text="Select Assessment" size="m" weight="ultraBold" useTranslationText={false} />
      <CustomText
        style={styles.subText}
        text="Choose the assessment this review will be associated with"
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="s" />

      {assessmentsLoading ? (
        <CustomText text="Loading assessments..." style={styles.loadingText} useTranslationText={false} />
      ) : !hasAssessments ? (
        <CustomText
          style={styles.errorText}
          text="No assessments available. Please create assessments first."
          useTranslationText={false}
        />
      ) : (
        <SearchSelectMulti
          name="assessmentId"
          control={control}
          label=""
          loading={assessmentsLoading}
          placeholder="Select an assessment"
          backgroundColor="#3E68FE"
          showAllByDefault={true}
          itemType="assessment"
          limitOne={true}
          fetchOptions={filterAssessments}
        />
      )}

      {/* Assessment Change Warning */}
      {showAssessmentChangeWarning && (
        <>
          <CustomSpacing type="vertical" size="s" />
          <View style={styles.warningContainer}>
            <CustomText
              text="⚠️ Warning: Changing Assessment"
              size="m"
              weight="bold"
              style={styles.warningTitle}
              useTranslationText={true}
            />
            <CustomText
              text="All existing student reviews will be deleted as they are no longer relevant to the new assessment. This action cannot be undone."
              size="s"
              style={styles.warningText}
              useTranslationText={true}
            />
          </View>
        </>
      )}

      <CustomSpacing type="vertical" size="m" />
      <View style={styles.activityState}>
        <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Publish this activity now or schedule a publish date."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />
        <Controller
          control={control}
          name="activityState"
          render={({ field: { onChange, value } }) => (
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              {Object.keys(ActivityState).map((state) => (
                <CustomButton
                  key={state}
                  text={ActivityState[state]}
                  size="s"
                  style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                  onPress={() => {
                    onChange(ActivityState[state]);
                  }}
                />
              ))}
            </div>
          )}
        />
        {watchActivityState === ActivityState.Scheduled && (
          <>
            <CustomText
              text="This Activity will automatically be published after:"
              size="s"
              useTranslationText={false}
              style={styles.stepDescription}
            />
            <Controller
              control={control}
              name="scheduledDate"
              render={({ field: { onChange, value } }) => (
                <div style={{ maxWidth: 250, marginTop: 10 }}>
                  <DatePickerInput
                    locale="en"
                    value={value instanceof Date ? value : value ? new Date(value) : undefined}
                    onChange={onChange}
                    inputMode="start"
                    mode="outlined"
                    outlineColor={"#D1D5DB"}
                    selectionColor={undefined}
                    activeOutlineColor={"blue"}
                    withDateFormatInLabel={false}
                    placeholder="MM/DD/YYYY"
                    theme={{
                      colors: {
                        primary: "#000000",
                        onSurface: "#667085",
                      },
                      roundness: 8,
                    }}
                    style={styles.datePicker}
                    aria-label="Schedule published date picker"
                  />
                </div>
              )}
            />
          </>
        )}
      </View>

      {errors.assessmentId && (
        <CustomText text="Assessment selection is required" style={styles.errorText} useTranslationText={false} />
      )}
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  loadingText: {
    color: "#667085",
    marginTop: 8,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
  warningContainer: {
    backgroundColor: "#FEF3C7",
    borderColor: "#F59E0B",
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  warningTitle: {
    color: "#92400E",
    marginBottom: 4,
  },
  warningText: {
    color: "#92400E",
    lineHeight: 18,
  },
});

export default ReviewForm;
